import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Chip,
    TextField,
    Select,
    MenuItem,
    FormControl,
    FormControlLabel,
    Switch,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles, createStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(() =>
    createStyles({
        field: { marginBottom: 12 },
    })
);

export interface EntryFormValues {
    title: string;
    amount: string;
    type: 'DEBIT' | 'CREDIT';
    category: string;
    tag: string;
    date: string;
    onCredit?: boolean;
}

interface EntryFormDialogProps {
    open: boolean;
    dialogTitle: string;
    saveLabel?: string;
    initialValues?: Partial<EntryFormValues>;
    existingTags?: string[];
    existingCategories?: string[];
    onSave: (data: EntryFormValues) => void;
    onClose: () => void;
}

export const EntryFormDialog: React.FC<EntryFormDialogProps> = ({
    open,
    dialogTitle,
    saveLabel = 'Save',
    initialValues,
    existingTags = [],
    existingCategories = [],
    onSave,
    onClose,
}) => {
    const classes = useStyles();
    const [form, setForm] = useState<EntryFormValues>({
        title: '',
        amount: '',
        type: 'DEBIT',
        category: '',
        tag: '',
        date: '',
        onCredit: false,
    });

    // Re-initialise whenever the dialog opens with new values
    useEffect(() => {
        if (open) {
            setForm({
                title: initialValues?.title ?? '',
                amount: initialValues?.amount ?? '',
                type: initialValues?.type ?? 'DEBIT',
                category: initialValues?.category ?? '',
                tag: initialValues?.tag ?? '',
                date: initialValues?.date ?? '',
                onCredit: initialValues?.onCredit ?? false,
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleSave = () => {
        if (!form.title.trim() || !form.amount) return;
        onSave(form);
    };

    const set = (key: keyof EntryFormValues) => (e: React.ChangeEvent<any>) =>
        setForm(f => ({ ...f, [key]: e.target.value }));

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogContent>
                <Box mt={1}>
                    <TextField
                        label="Description"
                        value={form.title}
                        onChange={set('title')}
                        fullWidth
                        size="small"
                        variant="outlined"
                        className={classes.field}
                        autoFocus
                    />
                    <Box display="flex" style={{ gap: 8 }} className={classes.field}>
                        <FormControl variant="outlined" size="small" style={{ minWidth: 130 }}>
                            <Select
                                value={form.type}
                                onChange={e => setForm(f => ({ ...f, type: e.target.value as 'DEBIT' | 'CREDIT' }))}
                            >
                                <MenuItem value="DEBIT">Debit (−)</MenuItem>
                                <MenuItem value="CREDIT">Credit (+)</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label="Amount"
                            type="number"
                            value={form.amount}
                            onChange={set('amount')}
                            fullWidth
                            size="small"
                            variant="outlined"
                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                        />
                    </Box>
                    <Autocomplete
                        multiple
                        freeSolo
                        size="small"
                        options={existingCategories}
                        value={form.category ? form.category.split(',').map(s => s.trim()).filter(Boolean) : []}
                        onChange={(_, newVal) => setForm(f => ({ ...f, category: (newVal as string[]).join(',') }))}
                        renderTags={(val, getTagProps) =>
                            val.map((opt, i) => (
                                <Chip
                                    label={opt}
                                    size="small"
                                    color="primary"
                                    style={{ fontSize: '0.75rem', height: 24 }}
                                    {...getTagProps({ index: i })}
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Category"
                                variant="outlined"
                                placeholder="Type to search…"
                                className={classes.field}
                            />
                        )}
                    />
                    <Autocomplete
                        multiple
                        freeSolo
                        size="small"
                        options={existingTags}
                        value={form.tag ? form.tag.split(',').map(s => s.trim()).filter(Boolean) : []}
                        onChange={(_, newVal) => setForm(f => ({ ...f, tag: (newVal as string[]).join(',') }))}
                        renderTags={(val, getTagProps) =>
                            val.map((opt, i) => (
                                <Chip
                                    label={opt}
                                    size="small"
                                    color="primary"
                                    style={{ fontSize: '0.75rem', height: 24 }}
                                    {...getTagProps({ index: i })}
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Tag"
                                variant="outlined"
                                placeholder="Type to search…"
                                className={classes.field}
                            />
                        )}
                    />
                    <TextField
                        label="Date"
                        type="date"
                        value={form.date}
                        onChange={set('date')}
                        fullWidth
                        size="small"
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        className={classes.field}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={!!form.onCredit}
                                onChange={e => setForm(f => ({ ...f, onCredit: e.target.checked }))}
                                color="primary"
                                size="small"
                            />
                        }
                        label="On credit"
                        style={{ marginLeft: 0 }}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={!form.title.trim() || !form.amount}
                    onClick={handleSave}
                >
                    {saveLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
